/**
 * /dashboard/posh/icc — ICC committee management (STRICT 2FA).
 *
 * Migration note (2026-06-01, Next.js → Vite, Phase 3d): port of
 * frontend/src/app/(dashboard)/dashboard/posh/icc/page.tsx.
 */

import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import {
  is2FARequiredError,
  useDeactivateICCMember,
  useICCMembers,
  useUpdateICCMember,
} from "@/hooks/use-posh";
import { fmtDate, iccMemberLabel } from "@/lib/posh-format";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { ICCMemberForm } from "@/components/posh/icc-member-form";
import { ICCCompositionCard } from "@/components/posh/icc-composition-card";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toast } from "sonner";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/icc",
)({
  component: ICCRoute,
});

function MembersSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-2 h-4 w-1/2" />
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function MembersList() {
  const q = useICCMembers();
  const deactivateMut = useDeactivateICCMember();
  const updateMut = useUpdateICCMember();

  if (q.isLoading) return <MembersSkeleton />;

  if (q.isError && is2FARequiredError(q.error)) {
    return <TwoFactorRequired surface="ICC committee management" />;
  }
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load ICC members</AlertTitle>
        <AlertDescription>
          {toApiError(q.error).message}
        </AlertDescription>
      </Alert>
    );
  }

  const members = q.data?.members ?? [];
  if (members.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No ICC members yet. Add the presiding officer and members to
          constitute the committee (POSH Act s.4).
        </CardContent>
      </Card>
    );
  }

  async function onDeactivate(id: number) {
    try {
      await deactivateMut.mutateAsync(id);
      toast.success("ICC member deactivated.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function onReactivate(id: number) {
    try {
      await updateMut.mutateAsync({ id, body: { is_active: true } });
      toast.success("ICC member reactivated.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <div className="space-y-3">
      {members.map((m) => (
        <Card key={m.id}>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base">
                  {iccMemberLabel(m)}
                </CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {m.is_external ? "External" : "Internal"} · Appointed{" "}
                  {fmtDate(m.appointed_date)}
                  {m.term_end_date
                    ? ` · Term ends ${fmtDate(m.term_end_date)}`
                    : ""}
                  {m.is_external && m.external_email
                    ? ` · ${m.external_email}`
                    : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge
                  tone={m.is_active ? "success" : "neutral"}
                  label={m.is_active ? "Active" : "Inactive"}
                />
                {m.is_active ? (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={deactivateMut.isPending}
                    onClick={() => void onDeactivate(m.id)}
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={updateMut.isPending}
                    onClick={() => void onReactivate(m.id)}
                  >
                    Reactivate
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function ICCRoute() {
  return (
    <PoshModuleShell
      title="ICC Members"
      description="Internal Complaints Committee composition. This area requires two-factor authentication."
      actions={<ICCMemberForm />}
    >
      <PoshFeatureGate>
        <div className="space-y-6">
          <ICCCompositionCard />
          <MembersList />
        </div>
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
